<template>
  <QmulusButton
    v-bind="{
      ...props,
      ...variantMap[variant],
    }"
    :label="label || variantMap[variant].label"
    :icon="variantMap[variant].icon"
    :data-qa="`semantic-button-${variant}`"
  />
</template>

<script setup lang="ts">
import {
  computed,
} from 'vue';

import useLibraryI18n from '../../i18n/composable.ts';

import {
  IQmulusButtonProps,
} from './qmulus-button';
import QmulusButton from './qmulus-button.vue';
import {
  SEMANTIC_BUTTON_ICON_MAP,
  TSemanticButtonVariant,
} from './semantic-button';

export interface ISemanticButtonProps extends Omit<IQmulusButtonProps, 'label'> {
  variant: TSemanticButtonVariant,
  label?: string,
}

const props = defineProps<ISemanticButtonProps>();

const {
  t,
} = useLibraryI18n('components/button/semantic-button');

const variantMap = computed<Record<TSemanticButtonVariant, IQmulusButtonProps>>(() => {
  return {
    close: {
      label: t('close'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'secondary',
      icon: (props.iconOnly || props.isInlineAction) ? SEMANTIC_BUTTON_ICON_MAP.close : undefined,
    },
    cancel: {
      label: t('cancel'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'secondary',
      icon: (props.iconOnly || props.isInlineAction) ? SEMANTIC_BUTTON_ICON_MAP.cancel : undefined,
    },
    back: {
      label: t('back'),
      severity: 'tertiary',
      icon: (props.iconOnly || props.isInlineAction) ? SEMANTIC_BUTTON_ICON_MAP.back : undefined,
    },
    forward: {
      label: t('forward'),
      severity: 'tertiary',
      icon: (props.iconOnly || props.isInlineAction) ? SEMANTIC_BUTTON_ICON_MAP.forward : undefined,
    },
    edit: {
      label: t('edit'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.edit,
    },
    save: {
      label: t('save'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.save,
    },
    accept: {
      label: t('accept'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.accept,
    },
    saveAsNew: {
      label: t('saveAsNew'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.saveAsNew,
    },
    delete: {
      label: t('delete'),
      severity: 'danger',
      icon: SEMANTIC_BUTTON_ICON_MAP.delete,
    },
    reset: {
      label: t('reset'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.reset,
      isInlineAction: true,
    },
    resetFilters: {
      label: t('resetFilters'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.resetFilters,
      iconOnly: true,
    },
    add: {
      label: t('add'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.add,
    },
    create: {
      label: t('create'),
      severity: props.isInlineAction
        ? 'tertiary'
        : 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.create,
    },
    settings: {
      label: t('settings'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.settings,
      iconOnly: true,
    },
    tableNavigation: {
      label: t('tableNavigation'),
      severity: 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.tableNavigation,
      iconOnly: true,
      isInlineAction: true,
    },
    upload: {
      label: t('upload'),
      severity: 'primary',
      icon: SEMANTIC_BUTTON_ICON_MAP.upload,
    },
    calculate: {
      label: t('calculate'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.calculate,
      iconOnly: true,
      isInlineAction: false,
    },
    revision: {
      label: t('revision'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.revision,
      iconOnly: true,
      isInlineAction: false,
    },
    fullscreen: {
      label: t('fullscreen'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.fullscreen,
      iconOnly: true,
      isInlineAction: true,
    },
    optionMenu: {
      label: t('optionMenu'),
      severity: 'tertiary',
      icon: SEMANTIC_BUTTON_ICON_MAP.optionMenu,
      iconOnly: true,
      isInlineAction: true,
    },
  };
});

</script>
